import {FormEvent} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import {useReportEditor} from '../hooks/use-report-editor';
import {useReportNavigationGuard} from '../hooks/use-report-navigation-guard';
import {ReportFieldsEditor} from './report-fields-editor';
import styles from './report-editor.module.scss';

const ReportEditor = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const editor = useReportEditor(id);
  const blocker = useReportNavigationGuard(editor.dirty, editor.allowNavigation);
  const {values, errors, saving} = editor;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void editor.save();
  };
  const back = () => navigate('/reports/builder');

  return (
    <div className={styles.page}>
      <Button startIcon={<ArrowBackIcon />} onClick={back} disabled={saving} sx={{mb: 1.5}}>
        Конструктор отчётов
      </Button>
      <h1>{editor.isEdit ? 'Редактирование отчёта' : 'Создание отчёта'}</h1>
      {editor.loading ? (
        <div className={styles.loading} role="status">
          <CircularProgress size={28} />
          <span>Загрузка редактора…</span>
        </div>
      ) : editor.accessError ? (
        <Alert severity="info">{editor.accessError}</Alert>
      ) : editor.loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={editor.retry}>
              Повторить
            </Button>
          }
        >
          {editor.loadError}
        </Alert>
      ) : (
        <form className={styles.form} onSubmit={submit} noValidate>
          <div className={styles.details}>
            <TextField
              label="Название отчёта"
              required
              fullWidth
              size="small"
              value={values.name}
              disabled={saving}
              error={Boolean(errors.name)}
              helperText={errors.name}
              inputProps={{maxLength: 127}}
              onChange={(event) => editor.updateValues({...values, name: event.target.value})}
            />
            <TextField
              label="Комментарий"
              sx={{gridColumn: '1 / -1'}}
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={values.comment}
              disabled={saving}
              error={Boolean(errors.comment)}
              helperText={errors.comment}
              inputProps={{maxLength: 1023}}
              onChange={(event) => editor.updateValues({...values, comment: event.target.value})}
            />
            <Stack direction="row" sx={{width: 'fit-content'}}>
              <FormControlLabel
                className={styles.publicSwitch}
                label="Доступен коллегам в компании"
                control={
                  <Switch
                    checked={values.isPublic}
                    disabled={saving}
                    onChange={(_, isPublic) => editor.updateValues({...values, isPublic})}
                  />
                }
              />
            </Stack>
          </div>
          <h2>Поля отчёта</h2>
          <p className={styles.fieldsHint}>Выберите поля и задайте порядок их отображения в отчёте</p>
          <ReportFieldsEditor
            fields={editor.fields}
            columns={values.columns}
            errors={errors.columnErrors}
            disabled={saving}
            onChange={(columns) => editor.updateValues({...values, columns})}
          />
          {(errors.columns || errors.body) && (
            <Alert severity="error" sx={{mt: 2}}>
              {errors.columns || errors.body}
            </Alert>
          )}
          {editor.saveError && (
            <Alert severity="error" sx={{mt: 2}}>
              {editor.saveError}
            </Alert>
          )}
          <div className={styles.footer}>
            {editor.dirty && <span className={styles.dirty}>Есть несохранённые изменения</span>}
            <div className={styles.footerButtons}>
              <Button variant="outlined" onClick={back} disabled={saving}>
                Отмена
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saving ? 'Сохранение…' : editor.isEdit ? 'Сохранить изменения' : 'Создать отчёт'}
              </Button>
            </div>
          </div>
        </form>
      )}
      <Dialog
        open={blocker.state === 'blocked'}
        onClose={saving ? undefined : () => blocker.state === 'blocked' && blocker.reset()}
        aria-labelledby="report-unsaved-title"
      >
        <DialogTitle id="report-unsaved-title">Выйти без сохранения?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {saving ? 'Дождитесь сохранения отчёта.' : 'Изменения отчёта будут потеряны.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.state === 'blocked' && blocker.reset()}>Остаться</Button>
          <Button color="error" disabled={saving} onClick={() => blocker.state === 'blocked' && blocker.proceed()}>
            Выйти без сохранения
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReportEditor;
